import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';
import * as moment from 'moment';

import {
    ComponentDefinition,
    Dictionary,
    DigitalSet,
    EditSpotValueWithOptions,
    IDateRangePickerOptions,
    KeyValuePair,
    NameBuilderConfiguration,
    SpotValue,
    TagDefinition
} from '../../models';
import { SpotManualInputsWidget } from './spotManualInputs.widget';
import { HeadingDefinition } from '../logbookTable/HeadingDefinition';
import { ISpotValueHelperService } from '../../services/';
import { SpotManualInputsConfiguration } from './spotManualInputs.configuration';

interface ViewByDate {
    body: BodyRow[];
    headings: HeadingDefinition[][];
}

interface SpotValueObject {
    spotValue: SpotValue;
}

interface BodyRow {
    hasLogbookEntry: boolean;
    momentTimestamp: moment.Moment;
    options: IDateRangePickerOptions;
    originalTimestamp: string | null;
    timestamp: string;
    values: EditSpotValueWithOptions[];
}

class SpotManualInputsController implements angular.IComponentController {
    dateOptions: IDateRangePickerOptions;
    formController: angular.IFormController;
    headings: HeadingDefinition[];
    tagDictionary: KeyValuePair<string, string>[];
    viewByDate: ViewByDate;

    readonly decimals: number = 2;
    readonly digitalSets: Dictionary<DigitalSet>;
    readonly isEditMode: boolean;
    readonly isLoading: boolean;
    readonly item: SpotManualInputsWidget;
    readonly onDeleteRow?: (sv: SpotValueObject) => void;
    readonly onValueChanged?: (sv: SpotValueObject) => void;
    readonly tags: TagDefinition[];
    readonly values: EditSpotValueWithOptions[];

    private _optionsWatcher?: () => void;

    static $inject = ['$scope', '$translate', 'spotValueHelperService'];

    constructor(
        private _$scope: angular.IScope,
        private _$translate: angular.translate.ITranslateService,
        private _spotValueHelperService: ISpotValueHelperService
    ) {}

    $onInit() {
        this.viewByDate = {
            body: [],
            headings: []
        };
        this.initwatcher();
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['isEditMode']) {
            if (this.isEditMode === false) {
                this.setPristine();
                if (this._optionsWatcher) {
                    this._optionsWatcher();
                }
            } else {
                this.initwatcher();
            }
        }
        if (changesObj['item'] && this.item) {
            this.initwatcher();
        }
        if (changesObj['tags'] && this.tags) {
            this.tagDictionary = this.tags.map((t) => {
                return { key: t.guid, value: t.name };
            });
            this.createTable(this.item.configuration);
        }

        if (changesObj['values'] && this.values && this.tags && this.digitalSets) {
            this.createTable(this.item.configuration);
        }
    }

    onChange(spotValue: EditSpotValueWithOptions, $index: number) {
        if (this.onValueChanged) {
            spotValue.title = this.getTitle(spotValue);
            this.onValueChanged({ spotValue });
        }
    }

    onTimestampChange(spotValue: EditSpotValueWithOptions, $index: number) {
        if (this.formController && this.formController['timestamp' + $index]) {
            this.formController['timestamp' + $index].$setDirty();
            const dates = this.values.map((v) => v.momentTimestamp.toISOString());
            const newDate = spotValue.momentTimestamp.toISOString();
            const matching = dates.filter((d) => d === newDate);
            const hasDuplicate = matching.length > 1;
            this.formController['timestamp' + $index].$setValidity('duplicate', !hasDuplicate);
        }
        this.onChange(spotValue, $index);
    }

    deleteRow(spotValue: EditSpotValueWithOptions) {
        if (this.onDeleteRow) {
            this.onDeleteRow({ spotValue });
        }
        if (this.formController) {
            this.formController.$setDirty();
        }
    }

    deleteRowByDate(row: BodyRow, $index: number) {
        if (this.onDeleteRow) {
            row.values.forEach((spotValue) => {
                this.onDeleteRow({ spotValue });
            });
        }
        if (this.formController) {
            this.formController.$setDirty();
        }
    }

    onTagChange(spotValue: EditSpotValueWithOptions, newValue: string) {
        const tagDefinition = this.tags.filter((t) => t.guid === newValue)[0];
        Object.assign(spotValue, this._spotValueHelperService.getDigitalProperties(tagDefinition, this.digitalSets));
    }

    private initwatcher() {
        this._optionsWatcher = this._$scope.$watch(
            () => this.item.configuration,
            () => this.createTable(this.item.configuration),
            true
        );
        if (this.item.configuration.is2DTable) {
            this._$scope.$watch(
                () => this.viewByDate.body,
                () => this.updateRowTimestamp(),
                true
            );
        }
    }

    private updateRowTimestamp() {
        if (this.viewByDate.body) {
            this.viewByDate.body.forEach((row) => {
                row.values.forEach((value) => {
                    value.momentTimestamp = moment(row.momentTimestamp);
                });
            });
        }
    }

    private createTable(configuration) {
        if (this.item.configuration.is2DTable) {
            this.viewByDate = {
                headings: this.buildHeadingByDate(configuration),
                body: this.buildBodyByDate()
            };
        } else {
            this.setHeadings();
        }
    }

    private getSpot(guid: string, values: EditSpotValueWithOptions[]): EditSpotValueWithOptions {
        const foundSpot = values?.find((value) => value.viewModel?.guid === guid);
        return foundSpot;
    }

    private buildHeadingByDate(configuration: SpotManualInputsConfiguration): HeadingDefinition[][] {
        const defaultHeading: HeadingDefinition[][] = [];
        if (this.values && this.tags) {
            const distinctGuids = _.uniq(this.values.filter((value) => !!value.viewModel?.guid).map((value) => value.viewModel?.guid));
            let currentRowIndex = 0;
            if (configuration.nameConfiguration.hasGroupName) {
                defaultHeading[currentRowIndex] = [];
                defaultHeading[currentRowIndex].push({ label: 'Group', style: {}, classed: { 'groupname-column': true } });
                if (distinctGuids) {
                    distinctGuids.forEach((guid) => {
                        const metadata = {
                            label: this.getSpot(guid, this.values)?.identifier?.groupUniqueName,
                            style: {},
                            classed: currentRowIndex > 0 ? { 'sub-heading': true } : {}
                        };
                        defaultHeading[currentRowIndex].push(metadata);
                    });
                }
                currentRowIndex++;
            }
            if (configuration.nameConfiguration.hasName) {
                defaultHeading[currentRowIndex] = [];
                defaultHeading[currentRowIndex].push({ label: 'Identifier', style: {}, classed: { 'identifiername-column': true } });
                if (distinctGuids) {
                    distinctGuids.forEach((guid) => {
                        const metadata = {
                            label: this.getSpot(guid, this.values)?.identifier?.name,
                            style: {},
                            classed: currentRowIndex > 0 ? { 'sub-heading': true } : {}
                        };
                        defaultHeading[currentRowIndex].push(metadata);
                    });
                }
                currentRowIndex++;
            }
            if (configuration.nameConfiguration.hasShortComment) {
                defaultHeading[currentRowIndex] = [];
                defaultHeading[currentRowIndex].push({ label: 'Identifier comment', style: {}, classed: { 'identifercomment-column': true } });
                if (distinctGuids) {
                    distinctGuids.forEach((guid) => {
                        const metadata = {
                            label: this.getSpot(guid, this.values)?.identifier?.shortComment,
                            style: {},
                            classed: currentRowIndex > 0 ? { 'sub-heading': true } : {}
                        };
                        defaultHeading[currentRowIndex].push(metadata);
                    });
                }
                currentRowIndex++;
            }
            if (configuration.hasTagName) {
                defaultHeading[currentRowIndex] = [];
                defaultHeading[currentRowIndex].push({ label: 'Tag', style: {}, classed: { 'tagname-column': true } });
                if (distinctGuids) {
                    distinctGuids.forEach((guid) => {
                        const found = this.tags.find((tag) => tag.guid === guid);
                        const metadata: HeadingDefinition = {
                            label: '',
                            style: {},
                            classed: currentRowIndex > 0 ? { 'sub-heading': true } : {}
                        };
                        if (found) {
                            metadata.key = found.guid;
                            metadata.label = found.name;
                        }
                        defaultHeading[currentRowIndex].push(metadata);
                    });
                }
                currentRowIndex++;
            }
            if (configuration.nameConfiguration.hasTagShortComment) {
                defaultHeading[currentRowIndex] = [];
                defaultHeading[currentRowIndex].push({ label: 'Comment', style: {}, classed: { 'tagcomment-column': true } });
                if (distinctGuids) {
                    distinctGuids.forEach((guid) => {
                        const metadata = {
                            label: this.getSpot(guid, this.values)?.viewModel?.shortComment,
                            style: {},
                            classed: currentRowIndex > 0 ? { 'sub-heading': true } : {}
                        };
                        defaultHeading[currentRowIndex].push(metadata);
                    });
                }
                currentRowIndex++;
            }
            if (configuration.nameConfiguration.hasUnit) {
                defaultHeading[currentRowIndex] = [];
                defaultHeading[currentRowIndex].push({ label: 'Unit', style: {}, classed: { 'unitname-column': true } });
                if (distinctGuids) {
                    distinctGuids.forEach((guid) => {
                        const metadata = {
                            label: this.getSpot(guid, this.values)?.viewModel?.unitName,
                            style: {},
                            classed: currentRowIndex > 0 ? { 'sub-heading': true } : {}
                        };
                        defaultHeading[currentRowIndex].push(metadata);
                    });
                }
                currentRowIndex++;
            }
        }
        return defaultHeading;
    }

    hasMetadata(nameConfiguration: NameBuilderConfiguration) {
        return (
            nameConfiguration.hasGroupName ||
            nameConfiguration.hasName ||
            nameConfiguration.hasTagShortComment ||
            nameConfiguration.hasShortComment ||
            nameConfiguration.hasUnit
        );
    }

    private buildBodyByDate(): BodyRow[] {
        let nest: BodyRow[];
        if (this.tags && this.values) {
            const td = {};
            this.tags.forEach((t) => (td[t.guid] = t));
            const distinctGuids = _.uniq(this.values.filter((value) => !!value.viewModel?.guid).map((value) => value.viewModel?.guid));
            nest = d3
                .nest<EditSpotValueWithOptions>()
                .key((d) => d.timestamp)
                .entries(this.values)
                .map((d) => {
                    const { key } = d;
                    const spotTime = {
                        momentTimestamp: moment(key),
                        options: this._spotValueHelperService.getDateOptions(),
                        originalTimestamp: null,
                        timestamp: key
                    };
                    const values = distinctGuids.map((guid) => {
                        let found: EditSpotValueWithOptions = _.find(d.values, (v) => v.tagGuid === guid);
                        if (!found) {
                            const sv: SpotValue = {
                                tagGuid: guid,
                                comment: null,
                                dataTransactionId: null,
                                timestamp: null,
                                logbookEntryId: null,
                                value: null
                            };
                            found = this._spotValueHelperService.mapSpotValuesToEditSpotValuesWithOptions([sv], this.digitalSets, td, {}, (s, i) => {})[0];
                            found.timestamp = spotTime.timestamp;
                            found.momentTimestamp = spotTime.momentTimestamp;
                            found.title = this._$translate.instant('No value yet');
                        }
                        return found;
                    });
                    return Object.assign(spotTime, {
                        hasLogbookEntry: values.filter((v) => !!v.logbookEntryId).length > 0,
                        originalTimestamp: values.filter((v) => !!v.originalTimestamp).length > 0 ? key : null,
                        values
                    });
                });
        } else {
            nest = [];
        }
        return nest;
    }

    private getTitle(spotValue: EditSpotValueWithOptions): string {
        return this._spotValueHelperService.getTitle(spotValue, spotValue.isDigital, spotValue.digitalSetDictionary);
    }

    private setHeadings() {
        const { configuration } = this.item;
        if (!configuration.is2DTable) {
            const headings: HeadingDefinition[] = [];
            const weights: number[] = [];
            if (configuration.nameConfiguration.hasGroupName) {
                headings.push({ label: 'Group', style: {}, classed: { 'groupname-column': true } });
                weights.push(1);
            }
            if (configuration.hasTimeStamp) {
                headings.push({ label: 'TimeStamp', style: {}, classed: { 'timestamp-column': true } });
                weights.push(2);
            }
            if (configuration.nameConfiguration.hasName) {
                headings.push({ label: 'Identifier', style: {}, classed: { 'identifiername-column': true } });
                weights.push(1);
            }
            if (configuration.nameConfiguration.hasShortComment) {
                headings.push({ label: 'Identifier comment', style: {}, classed: { 'identifiercomment-column': true } });
                weights.push(1);
            }
            if (configuration.hasTagName) {
                headings.push({ label: 'Tag', style: {}, classed: { 'tagname-column': true } });
                weights.push(2);
            }
            if (configuration.nameConfiguration.hasTagShortComment) {
                headings.push({ label: 'Comment', style: {}, classed: { 'tagcomment-column': true } });
                weights.push(1);
            }
            if (configuration.nameConfiguration.hasUnit) {
                headings.push({ label: 'Unit', style: {}, classed: { 'unitname-column': true } });
                weights.push(1);
            }
            if (configuration.hasValue) {
                headings.push({ label: 'Value', style: {}, classed: { 'value-column': true } });
                weights.push(2);
            }
            if (configuration.hasUserComment) {
                headings.push({ label: 'User comment', style: {}, classed: { 'comment-column': true } });
                weights.push(4);
            }
            const sum = d3.sum(weights);
            headings.forEach((heading, i) => {
                heading.style.width = (weights[i] / sum) * 100 + '%';
            });
            this.headings = headings;
        } else {
            const headings = this.buildHeadingByDate(configuration);
            this.viewByDate = Object.assign({}, this.viewByDate, { headings });
        }
    }

    private setPristine() {
        this.formController?.$setPristine();
    }
}

export const wtSpotManualInputs: ComponentDefinition = {
    componentId: 'wtSpotManualInputs',
    controller: SpotManualInputsController,
    template: require('./spotManualInputs.component.html'),
    controllerAs: 'vm',
    bindings: {
        digitalSets: '<',
        formController: '=?',
        isEditMode: '<',
        isLoading: '<?',
        item: '<',
        onDeleteRow: '&?',
        onValueChanged: '&?',
        selected: '<',
        tags: '<',
        values: '<'
    }
};
